import { lazy } from "react";
import type { ComponentType, LazyExoticComponent } from "react";

// lazyWithRetry — a drop-in replacement for React.lazy that survives a deploy.
//
// The problem it fixes (observed in prod 2026-06-21, Sentry "Unexpected token
// '<'"): our heavy routes are code-split into hashed chunks (#424). When a new
// build deploys to Vercel, the chunk filenames change. A browser tab that still
// has the OLD index.html loaded will, on navigating to a lazy route, request a
// now-deleted chunk. Vercel's SPA catch-all answers that request with
// index.html (HTTP 200, text/html), so the browser tries to parse HTML as
// JavaScript and throws `SyntaxError: Unexpected token '<'` / `ChunkLoadError`,
// crashing the route until the user manually refreshes.
//
// The fix: when a lazy import fails with a chunk-load-style error, do ONE full
// page reload to pull the fresh chunk manifest, then let the route load
// normally. A timestamp cooldown in sessionStorage prevents a reload loop if the
// chunk is genuinely broken (not merely stale) — after one recent reload we let
// the error surface to the ErrorBoundary instead of reloading again.

export const RELOAD_TS_KEY = "parcely.chunkReloadTs";
// If we already reloaded within this window and the import STILL fails, the
// chunk is genuinely broken — stop reloading and surface the error.
export const RELOAD_COOLDOWN_MS = 10_000;

export function isChunkLoadError(err: unknown): boolean {
  const e = err as { name?: string; message?: string } | undefined;
  const name = e?.name ?? "";
  const msg = (e?.message ?? "").toLowerCase();
  return (
    name === "ChunkLoadError" ||
    msg.includes("loading chunk") ||
    msg.includes("loading css chunk") ||
    msg.includes("dynamically imported module") ||
    msg.includes("unexpected token '<'") ||
    msg.includes("failed to fetch")
  );
}

// importWithChunkRetry runs the dynamic import and, on a chunk-load failure,
// reloads the page once (cooldown-guarded) to recover from a stale post-deploy
// chunk. Exported for unit testing; lazyWithRetry wraps it in React.lazy below.
export async function importWithChunkRetry<T>(factory: () => Promise<T>): Promise<T> {
  try {
    return await factory();
  } catch (err) {
    if (!isChunkLoadError(err)) {
      throw err; // a real module/runtime error — don't mask it with a reload
    }

    let lastReload = 0;
    try {
      lastReload = Number(window.sessionStorage.getItem(RELOAD_TS_KEY)) || 0;
    } catch {
      /* sessionStorage unavailable (private mode) — treat as "never reloaded" */
    }

    const now = Date.now();
    if (now - lastReload > RELOAD_COOLDOWN_MS) {
      try {
        window.sessionStorage.setItem(RELOAD_TS_KEY, String(now));
      } catch {
        /* ignore — best effort */
      }
      window.location.reload();
      // Hang until the reload takes over so React doesn't flash the
      // ErrorBoundary in the gap before navigation.
      return new Promise<T>(() => {});
    }

    // Already reloaded once very recently and it's still failing → the chunk is
    // genuinely unavailable, not just stale. Let the ErrorBoundary handle it.
    throw err;
  }
}

export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(() => importWithChunkRetry(factory));
}
